import React, { useState, useEffect, useCallback } from 'react';
import { useWeddingContext } from '../context/WeddingContext';
import FamilyGroupManager from './FamilyGroupManager';
import { PRESET_TASK_TEMPLATES, generateTaskFromTemplate } from '../constants/presetTasks';

interface SettingsProps {
  user: any;
  signOut: () => void;
}

const Settings: React.FC<SettingsProps> = ({ user, signOut }) => {
  const { 
    settings, 
    updateSettings, 
    tasks, 
    isLoading, 
    error,
    clearError,
    refreshFamilyGroup,
    refreshTasks,
    addTask
  } = useWeddingContext();
  
  const [weddingDate, setWeddingDate] = useState(settings.weddingDate || '');
  const [venue, setVenue] = useState(settings.venue || '');
  const [budget, setBudget] = useState(settings.budget || 0);
  const [estimatedGuests, setEstimatedGuests] = useState(settings.estimatedGuests || 0);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);


  // 設定の値を初期化（settingsが更新されたときに反映）
  useEffect(() => {
    setWeddingDate(settings.weddingDate || '');
    setVenue(settings.venue || '');
    setBudget(settings.budget || 0);
    setEstimatedGuests(settings.estimatedGuests || 0);
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!weddingDate) {
      setMessage({ type: 'error', text: '挙式日を入力してください' });
      return;
    }

    try {
      const success = await updateSettings({
        weddingDate,
        venue,
        budget: Number(budget),
        estimatedGuests: Number(estimatedGuests)
      });

      if (success) {
        setMessage({ type: 'success', text: '設定が正常に保存されました！' });
      } else {
        setMessage({ type: 'error', text: '設定の保存に失敗しました' });
      }
      
      // 3秒後にメッセージを消去
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Settings update error:', error);
      setMessage({ type: 'error', text: '設定の保存に失敗しました' });
    }
  };

  const formatWeddingDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  // データエクスポート（簡易版）
  const handleExportData = () => {
    const data = {
      tasks: tasks,
      settings: settings,
      exportDate: new Date().toISOString()
    };
    
    const jsonData = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wedding-tasks-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ファミリーグループ変更時のコールバック（無限ループを防ぐため削除）
  // const handleFamilyGroupChange = useCallback(() => {
  //   // 初期読み込み時のイベントを受け取るだけで、追加のアクションは行わない
  //   console.log('Family group change event received (no action to prevent infinite loop)');
  // }, []);

  // ファミリー名更新時のコールバック（ヘッダー更新用）
  const handleFamilyNameUpdated = useCallback(async () => {
    try {
      console.log('Family name updated, refreshing header data...');
      await refreshFamilyGroup();
    } catch (error) {
      console.error('Failed to refresh family group after name update:', error);
    }
  }, [refreshFamilyGroup]);

  // プリセットタスク生成機能
  const handleGeneratePresetTasks = async () => {
    if (!weddingDate) {
      setMessage({ type: 'error', text: '挙式日を先に設定してください' });
    setTimeout(() => setMessage(null), 3000);
      return;
    }

    const confirmMessage = `【2024年最新版】市場調査に基づく包括的なプリセットタスク（${PRESET_TASK_TEMPLATES.length}項目）を生成します。\nデジタル化・効率化要素を含む完全版です。\n既存のプリセットタスクは重複せずスキップされます。\n\nよろしいですか？`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setMessage({ type: 'success', text: 'プリセットタスクを生成中...' });
      
      let successCount = 0;
      let errorCount = 0;

      // 既存のプリセットタスクがあるかチェック
      const existingPresetTasks = tasks.filter(task => task.isPreset);
      
      // 各プリセットタスクテンプレートからタスクを生成
      for (const template of PRESET_TASK_TEMPLATES) {
        try {
          const taskData = generateTaskFromTemplate(template, weddingDate);
          
          // 既存の同じプリセットタスクがある場合はスキップ
          const existingTask = existingPresetTasks.find(task => 
            task.presetTemplateId === template.templateId
          );
          
          if (!existingTask) {
            await addTask(taskData);
            const success = true;
            if (success) {
              successCount++;
            } else {
              errorCount++;
            }
          }
        } catch (error) {
          console.error(`Error creating preset task ${template.templateId}:`, error);
          errorCount++;
        }
      }

      // 結果をユーザーに通知
      if (successCount > 0) {
        setMessage({ 
          type: 'success', 
          text: `プリセットタスクを${successCount}個生成しました！${errorCount > 0 ? ` (${errorCount}個はエラー)` : ''}` 
        });
        
        // タスクリストを更新
        await refreshTasks();
      } else {
        setMessage({ 
          type: errorCount > 0 ? 'error' : 'success', 
          text: errorCount > 0 ? 'プリセットタスクの生成に失敗しました' : '既に全てのプリセットタスクが存在します'
        });
      }
      
    } catch (error) {
      console.error('Error generating preset tasks:', error);
      setMessage({ type: 'error', text: 'プリセットタスクの生成に失敗しました' });
    }

    setTimeout(() => setMessage(null), 5000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6 pb-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">⚙️ 設定</h2>
      
      {/* エラー表示 */}
      {error && (
        <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button 
            onClick={clearError}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* ファミリーアカウント管理セクション */}
      <section className="mb-6 sm:mb-8">
        <FamilyGroupManager 
          onFamilyNameUpdated={handleFamilyNameUpdated}
        />
      </section>
      
      <section className="mb-6 sm:mb-8 p-4 sm:p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-xl sm:text-2xl font-semibold text-pink-500 mb-3 sm:mb-4">結婚式設定</h2>
        
        {message && (
          <div className={`p-3 sm:p-4 rounded-lg mb-3 sm:mb-4 text-sm ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label htmlFor="wedding-date-input" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                挙式日:
              </label>
              <input
                type="date"
                id="wedding-date-input"
                value={weddingDate}
                onChange={(e) => setWeddingDate(e.target.value)}
                className="p-2 sm:p-3 w-full border border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none text-sm sm:text-base"
              />
            </div>
            
            <div>
              <label htmlFor="venue-input" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                会場名:
              </label>
              <input
                type="text"
                id="venue-input"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="結婚式場名"
                className="p-2 sm:p-3 w-full border border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none text-sm sm:text-base"
              />
            </div>
            
            <div>
              <label htmlFor="budget-input" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                予算（円）:
              </label>
              <input
                type="number"
                id="budget-input"
                value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
                placeholder="0"
                min="0"
                className="p-2 sm:p-3 w-full border border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none text-sm sm:text-base"
              />
            </div>
            
            <div>
              <label htmlFor="guests-input" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                想定ゲスト数:
              </label>
              <input
                type="number"
                id="guests-input"
                value={estimatedGuests}
                onChange={(e) => setEstimatedGuests(Number(e.target.value))}
                placeholder="0"
                min="0"
                className="p-2 sm:p-3 w-full border border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-400 outline-none text-sm sm:text-base"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-pink-500 hover:bg-pink-600 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg shadow-sm transition duration-150 disabled:opacity-50 text-sm sm:text-base"
          >
            <i className="fas fa-save mr-2"></i>
            {isLoading ? '保存中...' : '設定を保存'}
          </button>
        </form>

        {settings.weddingDate && (
          <div className="mt-3 sm:mt-4 p-3 bg-pink-50 rounded-lg">
            <h3 className="font-semibold text-pink-800 mb-2 text-sm sm:text-base">現在の設定:</h3>
            <div className="text-xs sm:text-sm text-pink-700 space-y-1">
              <p>挙式日: {formatWeddingDate(settings.weddingDate)}</p>
              {settings.venue && <p className="break-words">会場: {settings.venue}</p>}
              {settings.budget > 0 && <p>予算: {settings.budget.toLocaleString()}円</p>}
              {settings.estimatedGuests > 0 && <p>想定ゲスト数: {settings.estimatedGuests}名</p>}
            </div>
          </div>
        )}
      </section>

      {/* 便利機能セクション */}
      <section className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-xl sm:text-2xl font-semibold text-pink-500 mb-3 sm:mb-4">🛠️ 便利機能</h2>
        
        <div className="space-y-2 sm:space-y-0 sm:flex sm:gap-3 sm:flex-wrap">
            <button
            onClick={handleGeneratePresetTasks}
            className="w-full sm:w-auto flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-600 hover:to-pink-700 transition-all duration-200 shadow-sm text-xs sm:text-sm"
            disabled={isLoading}
            >
            <i className="fas fa-magic mr-1 sm:mr-2"></i>
            <span className="whitespace-nowrap">プリセットタスク生成</span>
            </button>
            
            <button
            onClick={handleExportData}
            className="w-full sm:w-auto flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm text-xs sm:text-sm"
            >
            <i className="fas fa-download mr-1 sm:mr-2"></i>
            <span className="whitespace-nowrap">準備記録をエクスポート</span>
            </button>
            
            <button
            onClick={() => {
              setMessage({ type: 'success', text: 'タスクデータを最新の状態に更新しました！' });
              setTimeout(() => setMessage(null), 3000);
              refreshTasks();
            }}
            className="w-full sm:w-auto flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-sm text-xs sm:text-sm"
          >
            <i className="fas fa-sync-alt mr-1 sm:mr-2"></i>
            <span className="whitespace-nowrap">タスク更新</span>
            </button>
        </div>
      </section>


        </div>
    </div>
  );
};

export default Settings; 